<script lang="ts" setup>

import { Dayjs } from 'dayjs'
import { computed, ComputedRef, Ref, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { SmrStaySuggestionItem, SmrStaySuggestionWildcardChild, StaySuggestionSearch, SuggestionJustification } from '@/misc/types'
import DiagnosisForm from '@/stay-displayer/smr/rhs/diagnosis/DiagnosisForm.vue'
import RhsDiagnosisItem from '@/stay-displayer/smr/rhs/RhsDiagnosisItem.vue'
import {
  Diagnosis,
  DiagnosisType,
  getSingleDiagnosisTypes,
  PredictedLabel,
  SuggestedDiagnosis
} from '@/store/modules/diagnosis/types'
import { PmsiTypeEnum } from '@/store/modules/settings/types'
import { RhsDashboardClass, RhsDashboardType, SmrDiagnosisGroup } from '@/store/modules/stay/smr/types'
import { StayHealthData } from '@/store/modules/stay/types'

const { t } = useI18n()

const props = withDefaults(defineProps<{
  rhsId: number
  rhsEndDate: Dayjs
  currentModeDiagnosisGroup: SmrDiagnosisGroup
  predictedDiagnoses: PredictedLabel[]
  healthData: StayHealthData
  suggestionJustifications?: Record<string, SuggestionJustification> | null
  // Optional: fetched separately from the main stay payload and merged in once it resolves.
  staySuggestions?: SmrStaySuggestionItem[]
}>(), {
  suggestionJustifications: null,
  staySuggestions: () => [],
})

const currentFormDisplayed: Ref<DiagnosisType | null> = ref(null)

const diagnosisTypesList = computed(() => getSingleDiagnosisTypes(PmsiTypeEnum.SMR, props.rhsEndDate))

const currentDiagnoses = computed(() => {
  const diagnoses = {}

  for (const diagnosisType of diagnosisTypesList.value) {
    diagnoses[diagnosisType] = null
    if(diagnosisType.toLowerCase() in props.currentModeDiagnosisGroup
      && props.currentModeDiagnosisGroup[diagnosisType.toLowerCase()] !== null
      && props.currentModeDiagnosisGroup[diagnosisType.toLowerCase()].associatedDiagnosis !== null
    ) {
      diagnoses[diagnosisType] = {
        ...props.currentModeDiagnosisGroup[diagnosisType.toLowerCase()].associatedDiagnosis,
        id: props.currentModeDiagnosisGroup[diagnosisType.toLowerCase()].id
      }
    }
  }

  diagnoses[DiagnosisType.DAS] = []

  for(const dasDiagnosis of props.currentModeDiagnosisGroup.dasList) {
    diagnoses[DiagnosisType.DAS].push({
      ...dasDiagnosis.associatedDiagnosis,
      id: dasDiagnosis.id,
    })
  }

  // Alphabetical order
  diagnoses[DiagnosisType.DAS] = (diagnoses[DiagnosisType.DAS] as Diagnosis[]).sort((diagnosis1, diagnosis2) =>
    diagnosis1.reference.localeCompare(diagnosis2.reference)
  )

  return diagnoses
})

const diagnosisSuggestionTypes = computed(() => [...diagnosisTypesList.value, DiagnosisType.DAS])

const currentDiagnosisReferences = computed(() => [
  ...currentDiagnoses.value[DiagnosisType.DAS],
  ...diagnosisTypesList.value
    .reduce((acc, type) => ([...acc, ...(currentDiagnoses.value[type] ? [currentDiagnoses.value[type]] : [])]), [])
].map((diagnosis) => diagnosis.reference))

// The backend aggregates wildcard-matched searches on the parent item, not on each wildcard child
// (a real diagnosis matched only via the wildcard pattern has its own `searches` empty — see
// SuggestionRepository::getSuggestionsMcoForStay's searches_agg, grouped by the wildcard parent
// reference) — every child is suggested by the parent's searches plus any of its own.
function realSuggestionItems(item: SmrStaySuggestionItem): (SmrStaySuggestionItem | SmrStaySuggestionWildcardChild)[] {
  if (!item.isWildcard) {
    return [item]
  }
  return item.wildcardChildren.map((child) => ({ ...child, searches: [...item.searches, ...child.searches] }))
}

function resolvePredictions(type: string, reference: string): PredictedLabel[] {
  return props.predictedDiagnoses.filter((label) => label.type === type && label.diagnosis.reference === reference)
}

// SMR predictions are not populated by the backend yet (FT_CODAGE-331: every staySuggestions item's
// `predictions` stays empty for SMR), so this list is empty in practice. Existence is still checked
// against staySuggestions rather than predictedDiagnoses directly, so this activates with no further
// frontend change the day the backend implements it — same existence-from-backend/hydration-from-
// local-data split as the search-based suggestions below. Hydration goes through predictedDiagnoses
// (this RHS's own predicted labels) since a suggestion item's `predictions` don't carry the
// diagnosis metadata (description/level) needed to render the card.
const aiSuggestedDiagnoses: ComputedRef<PredictedLabel[]> = computed(() => {
  const predicted: PredictedLabel[] = []

  for (const item of props.staySuggestions) {
    if (!diagnosisSuggestionTypes.value.includes(item.type as DiagnosisType)) {
      continue
    }

    for (const real of realSuggestionItems(item)) {
      if (!real.predictions.length || currentDiagnosisReferences.value.includes(real.reference)) {
        continue
      }
      predicted.push(...resolvePredictions(item.type, real.reference))
    }
  }

  return predicted
})

// Groups the searches suggesting each (not already coded) real diagnosis reference. Existence,
// wildcard resolution and the search-reference/type/reference correlation all come from the backend
// (SuggestionRepository::getSuggestionsForSmrStay already aggregates searches per type+reference), so
// no further client-side matching against a suggestedLabel is needed — description/level/canBeXXX are
// resolved server-side from the PMSI nomenclature (see ListSmrStaySuggestionsUseCase).
const suggestionsByDiagnosis = computed(() => {
  const byDiag = {} as { [key: string]: { [key: string]: SuggestedDiagnosis[] } }

  for (const item of props.staySuggestions) {
    if (!diagnosisSuggestionTypes.value.includes(item.type as DiagnosisType)) {
      continue
    }

    for (const real of realSuggestionItems(item)) {
      if (currentDiagnosisReferences.value.includes(real.reference) || !real.searches.length) {
        continue
      }

      byDiag[real.reference] = byDiag[real.reference] ?? {}
      byDiag[real.reference][item.type] = real.searches.map((search) => ({
        reference: real.reference,
        description: real.description ?? undefined,
        level: real.level ?? undefined,
        canBeFpp: real.canBeFpp,
        canBeMmp: real.canBeMmp,
        canBeAe: real.canBeAe,
        canBeDas: real.canBeDas,
        search,
        suggestedType: item.type,
      }))
    }
  }

  return byDiag
})

// Kept on staySuggestions (not filtered to non-coded references like suggestionsByDiagnosis above):
// looks up which searches suggested an already-coded diagnosis, by reference only, regardless of
// suggestion type — the suggestions endpoint returns every match, coded or not.
const searchesByDiagnosisReference = computed(() => {
  const byReference = {} as { [reference: string]: Map<number, StaySuggestionSearch> }

  for (const item of props.staySuggestions) {
    if (!diagnosisSuggestionTypes.value.includes(item.type as DiagnosisType)) {
      continue
    }

    for (const real of realSuggestionItems(item)) {
      byReference[real.reference] = byReference[real.reference] ?? new Map()
      for (const search of real.searches) {
        byReference[real.reference].set(search.sancareId, search)
      }
    }
  }

  return byReference
})

const hasSelectableDiagnoses = aiSuggestedDiagnoses.value.length || Object.keys(suggestionsByDiagnosis.value).length

function getPredictionConfidence(reference: string) {
  return props.predictedDiagnoses.find((diag) => diag['diagnosis']['reference'] === reference)?.confidence ?? null
}

const setDisplayedForm = (diagnosisType: DiagnosisType, displayForm: boolean) => {
  currentFormDisplayed.value = displayForm ? diagnosisType : null
}

function getCodedDiagnosisSuggestingSearches(reference: string): StaySuggestionSearch[] {
  return Array.from(searchesByDiagnosisReference.value[reference]?.values() ?? [])
}

</script>

<template>
  <div class="col-lg-6 col-md-12 p-0 d-flex flex-column gap-2">
    <section
      v-for="diagnosisType in diagnosisTypesList"
      :key="diagnosisType"
      class="dashboard"
      :class="RhsDashboardClass[RhsDashboardType.RHS_DASHBOARD_DIAGNOSES]"
    >
      <div class="dashboard-title d-flex justify-content-between">
        <small>{{ diagnosisType }}</small>
        <small>{{ t('stayDisplayer.smr.rhs.codingInProgress') }}</small>
      </div>
      <rhs-diagnosis-item
        v-if="currentDiagnoses[diagnosisType]"
        :diagnosis="currentDiagnoses[diagnosisType]"
        :diagnosis-type="diagnosisType"
        :confidence="getPredictionConfidence(currentDiagnoses[diagnosisType]['reference'])"
        :is-suggestion="false"
        :rhs-id="rhsId"
        :rhs-end-date="rhsEndDate"
        :searches="getCodedDiagnosisSuggestingSearches(currentDiagnoses[diagnosisType]['reference'])"
        :health-data="healthData"
        :suggestion-justifications="suggestionJustifications"
      />
      <div>
        <small
          v-if="!currentDiagnoses[diagnosisType] && currentFormDisplayed !== diagnosisType"
          class="aligned-row fst-italic"
        >
          {{ t('stayDisplayer.smr.rhs.noDiagnosis', { diagnosisType }) }}
        </small>

        <diagnosis-form
          :diagnosis="currentDiagnoses[diagnosisType]"
          :diagnosis-type="diagnosisType"
          :rhs-id="rhsId"
          :rhs-end-date="rhsEndDate"
          :current-diagnosis-type-form-displayed="currentFormDisplayed"
          @display-form="displayForm => setDisplayedForm(diagnosisType, displayForm)"
        />
      </div>
    </section>

    <section
      class="dashboard"
      :class="RhsDashboardClass[RhsDashboardType.RHS_DASHBOARD_DIAGNOSES]"
    >
      <div class="dashboard-title d-flex justify-content-between">
        <small>{{ DiagnosisType.DAS }}</small>
        <small>{{ t('stayDisplayer.smr.rhs.codingInProgress') }}</small>
      </div>
      <rhs-diagnosis-item
        v-for="diagnosis in currentDiagnoses[DiagnosisType.DAS]"
        :key="`das-diagnosis-${diagnosis.id}`"
        :diagnosis="diagnosis"
        :diagnosis-type="DiagnosisType.DAS"
        :confidence="getPredictionConfidence(diagnosis['reference'])"
        :is-suggestion="false"
        :rhs-id="rhsId"
        :rhs-end-date="rhsEndDate"
        :searches="getCodedDiagnosisSuggestingSearches(diagnosis['reference'])"
        :health-data="healthData"
        :suggestion-justifications="suggestionJustifications"
      />
      <div
        v-if="!Object.keys(currentDiagnoses[DiagnosisType.DAS]).length
          && currentFormDisplayed !== DiagnosisType.DAS"
      >
        <small class="aligned-row fst-italic">
          {{ t('stayDisplayer.smr.rhs.noDiagnosis', { diagnosisType: DiagnosisType.DAS }) }}
        </small>
      </div>

      <diagnosis-form
        :diagnosis="currentDiagnoses[DiagnosisType.DAS]"
        :diagnosis-type="DiagnosisType.DAS"
        :rhs-id="rhsId"
        :rhs-end-date="rhsEndDate"
        :current-diagnosis-type-form-displayed="currentFormDisplayed"
        @display-form="displayForm => setDisplayedForm(DiagnosisType.DAS, displayForm)"
      />
    </section>
  </div>

  <div class="col-lg col-md-12 p-0">
    <section class="dashboard">
      <div class="dashboard-title">
        <small>{{ t('stayDisplayer.diagnoses', 2) }} - {{ t('stayDisplayer.sancareSuggestion', aiSuggestedDiagnoses.length) }}</small>
      </div>
      <rhs-diagnosis-item
        v-for="diagnosis in aiSuggestedDiagnoses"
        :key="`predicted-diagnosis-${diagnosis.id}`"
        :diagnosis="{ ...diagnosis['diagnosis'], id: diagnosis.id }"
        :confidence="diagnosis['confidence']"
        :is-suggestion="true"
        :rhs-id="rhsId"
        :rhs-end-date="rhsEndDate"
      />
      <div
        v-if="Object.keys(suggestionsByDiagnosis).length"
      >
        <template
          v-for="(byType, reference) in suggestionsByDiagnosis"
          :key="`suggested-diagnosis-${reference}`"
        >
          <rhs-diagnosis-item
            v-for="(suggestedDiagnoses, type) in byType"
            :key="`suggested-diagnosis-${type}-${reference}`"
            :diagnosis="suggestedDiagnoses[0]"
            :diagnosis-type="type"
            :confidence="null"
            :is-suggestion="true"
            :rhs-id="rhsId"
            :rhs-end-date="rhsEndDate"
            :searches="suggestedDiagnoses.map(diag => diag.search as StaySuggestionSearch)"
            :health-data="healthData"
            :suggestion-justifications="suggestionJustifications"
          />
        </template>
      </div>
      <small
        v-if="!hasSelectableDiagnoses"
        class="aligned-row fst-italic"
      >
        {{ t('stayDisplayer.smr.rhs.noSuggestedDiagnosis') }}
      </small>
    </section>
  </div>
</template>
